import type { CmmdAttachment, CmmdComment, CmmdTask } from "@cmmd-forge/contracts";
import { buildCmmdTaskGoal } from "@cmmd-forge/shared/cmmdTaskGoal";

import { buildTaskThreadContextPrompt } from "./command-center/taskThreadContext";
import type { ManualCommandTask, NoActiveThreadCommandTaskRow } from "./NoActiveThreadState.logic";

export interface TaskThreadStartPayload {
  readonly title: string;
  readonly seedPrompt: string;
  readonly cmmdTaskId: string | null;
  readonly goal?: string | null;
}

export interface BuildTaskThreadStartPayloadInput {
  readonly itemId: string;
  readonly manualTasks: readonly ManualCommandTask[];
  readonly commandTaskRows: readonly NoActiveThreadCommandTaskRow[];
  readonly cmmdTasks: readonly CmmdTask[];
  readonly cmmdProjects: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly campaignId?: string | null;
    readonly description?: string | null;
  }>;
  readonly cmmdCampaigns: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly description?: string | null;
  }>;
  readonly workspaceName: string | null;
  readonly identityDisplayName: string | null;
  readonly loadTaskComments: (taskId: string) => Promise<readonly CmmdComment[]>;
  readonly loadTaskAttachments: (taskId: string) => Promise<readonly CmmdAttachment[]>;
}

export async function buildTaskThreadStartPayload(
  input: BuildTaskThreadStartPayloadInput,
): Promise<TaskThreadStartPayload | null> {
  if (input.itemId.startsWith("manual:")) {
    return buildManualTaskThreadStartPayload(input);
  }

  return buildCmmdTaskThreadStartPayload({
    ...input,
    rawTaskId: input.itemId.startsWith("cmmd:") ? input.itemId.slice(5) : input.itemId,
  });
}

function buildManualTaskThreadStartPayload(
  input: BuildTaskThreadStartPayloadInput,
): TaskThreadStartPayload | null {
  const manual = input.manualTasks.find((task) => task.id === input.itemId);
  if (!manual) return null;

  return {
    title: manual.title,
    cmmdTaskId: null,
    seedPrompt: buildTaskThreadContextPrompt({
      workspaceName: input.workspaceName,
      identityDisplayName: input.identityDisplayName,
      campaignName: null,
      campaignDescription: null,
      projectName: manual.projectName ?? "Manual",
      projectDescription: null,
      task: {
        id: manual.id,
        title: manual.title,
        status: manual.status,
        priority: "medium",
        dueDate: null,
        description: null,
        dependsOn: [],
      },
      dependencyTitles: [],
    }),
  };
}

async function buildCmmdTaskThreadStartPayload(
  input: BuildTaskThreadStartPayloadInput & { readonly rawTaskId: string },
): Promise<TaskThreadStartPayload | null> {
  const context = resolveCmmdTaskContext(input);
  if (!context) return null;

  const { comments, attachments, contextWarnings } = await loadTaskContext(input, context.cmmdTask);

  return {
    title: context.title,
    cmmdTaskId: input.rawTaskId,
    goal:
      context.projectId === null
        ? null
        : buildCmmdTaskGoal({
            projectId: context.projectId,
            taskId: input.rawTaskId,
            title: context.title,
            description: context.task.description,
            updatedAt: context.updatedAt,
          }),
    seedPrompt: buildTaskThreadContextPrompt({
      workspaceName: input.workspaceName,
      identityDisplayName: input.identityDisplayName,
      campaignName: context.campaignName,
      campaignDescription: context.campaignDescription,
      projectName: context.projectName,
      projectDescription: context.projectDescription,
      task: context.task,
      dependencyTitles: resolveDependencyTitles(input.cmmdTasks, context.task.dependsOn),
      comments,
      attachments,
      contextWarnings,
    }),
  };
}

function resolveCmmdTaskContext(
  input: BuildTaskThreadStartPayloadInput & { readonly rawTaskId: string },
): {
  readonly cmmdTask: CmmdTask | null;
  readonly projectId: string | null;
  readonly updatedAt: string | null;
  readonly title: string;
  readonly campaignName: string | null;
  readonly campaignDescription: string | null;
  readonly projectName: string;
  readonly projectDescription: string | null;
  readonly task: {
    readonly id: string;
    readonly title: string;
    readonly status: string | null;
    readonly priority: string | null;
    readonly dueDate: string | null;
    readonly description: string | null;
    readonly dependsOn: ReadonlyArray<string>;
  };
} | null {
  const taskRow = input.commandTaskRows.find((row) => row.id === input.rawTaskId) ?? null;
  const cmmdTask = input.cmmdTasks.find((task) => task.id === input.rawTaskId) ?? null;
  if (!taskRow && !cmmdTask) return null;

  return buildResolvedCmmdTaskContext(input, taskRow, cmmdTask);
}

function buildResolvedCmmdTaskContext(
  input: BuildTaskThreadStartPayloadInput & { readonly rawTaskId: string },
  taskRow: NoActiveThreadCommandTaskRow | null,
  cmmdTask: CmmdTask | null,
) {
  const project = resolveTaskProject(input, cmmdTask);
  const campaign = resolveTaskCampaign(input, cmmdTask, project);
  const identity = resolveTaskContextIdentity(taskRow, cmmdTask);

  return {
    cmmdTask,
    projectId: identity.projectId,
    updatedAt: identity.updatedAt,
    title: resolveTaskTitle(input.rawTaskId, taskRow, cmmdTask),
    campaignName: resolveCampaignName(taskRow, campaign),
    campaignDescription: campaign?.description ?? null,
    projectName: resolveProjectName(taskRow, project),
    projectDescription: project?.description ?? null,
    task: resolvePromptTask(input.rawTaskId, taskRow, cmmdTask),
  };
}

function resolveTaskContextIdentity(
  taskRow: NoActiveThreadCommandTaskRow | null,
  cmmdTask: CmmdTask | null,
): { readonly projectId: string | null; readonly updatedAt: string | null } {
  return {
    projectId: cmmdTask?.projectId ?? taskRow?.projectId ?? null,
    updatedAt: cmmdTask?.updatedAt ?? taskRow?.updatedAt ?? null,
  };
}

function resolveTaskTitle(
  rawTaskId: string,
  taskRow: NoActiveThreadCommandTaskRow | null,
  cmmdTask: CmmdTask | null,
): string {
  return cmmdTask?.title ?? taskRow?.title ?? rawTaskId;
}

function resolveCampaignName(
  taskRow: NoActiveThreadCommandTaskRow | null,
  campaign: BuildTaskThreadStartPayloadInput["cmmdCampaigns"][number] | null,
): string | null {
  return campaign?.title ?? taskRow?.campaignName ?? null;
}

function resolveProjectName(
  taskRow: NoActiveThreadCommandTaskRow | null,
  project: BuildTaskThreadStartPayloadInput["cmmdProjects"][number] | null,
): string {
  return project?.title ?? taskRow?.projectName ?? "CMMD Project";
}

function resolvePromptTask(
  rawTaskId: string,
  taskRow: NoActiveThreadCommandTaskRow | null,
  cmmdTask: CmmdTask | null,
) {
  const fallback = resolveFallbackPromptTask(rawTaskId, taskRow);
  return {
    id: rawTaskId,
    title: cmmdTask?.title ?? fallback.title,
    status: cmmdTask?.status ?? fallback.status,
    priority: cmmdTask?.priority ?? fallback.priority,
    dueDate: cmmdTask?.dueDate ?? fallback.dueDate,
    description: cmmdTask?.description ?? null,
    dependsOn: cmmdTask?.dependsOn ?? fallback.dependsOn,
  };
}

function resolveFallbackPromptTask(
  rawTaskId: string,
  taskRow: NoActiveThreadCommandTaskRow | null,
): {
  readonly title: string;
  readonly status: string;
  readonly priority: string | null;
  readonly dueDate: string | null;
  readonly dependsOn: ReadonlyArray<string>;
} {
  return {
    title: taskRow?.title ?? rawTaskId,
    status: taskRow?.status ?? "open",
    priority: taskRow?.priority ?? null,
    dueDate: taskRow?.dueDate ?? null,
    dependsOn: taskRow?.dependsOn ?? [],
  };
}

function resolveTaskProject(
  input: BuildTaskThreadStartPayloadInput,
  cmmdTask: CmmdTask | null,
): BuildTaskThreadStartPayloadInput["cmmdProjects"][number] | null {
  if (!cmmdTask?.projectId) return null;
  return input.cmmdProjects.find((candidate) => candidate.id === cmmdTask.projectId) ?? null;
}

function resolveTaskCampaign(
  input: BuildTaskThreadStartPayloadInput,
  cmmdTask: CmmdTask | null,
  project: BuildTaskThreadStartPayloadInput["cmmdProjects"][number] | null,
): BuildTaskThreadStartPayloadInput["cmmdCampaigns"][number] | null {
  const campaignId = cmmdTask?.campaignId ?? project?.campaignId ?? null;
  if (!campaignId) return null;
  return input.cmmdCampaigns.find((candidate) => candidate.id === campaignId) ?? null;
}

function resolveDependencyTitles(
  cmmdTasks: readonly CmmdTask[],
  dependencyIds: ReadonlyArray<string>,
): readonly string[] {
  return dependencyIds.map((dependencyId) => {
    const dependency = cmmdTasks.find((task) => task.id === dependencyId);
    return dependency?.title ?? `Task ${dependencyId}`;
  });
}

async function loadTaskContext(
  input: BuildTaskThreadStartPayloadInput & { readonly rawTaskId: string },
  cmmdTask: CmmdTask | null,
): Promise<{
  readonly comments: readonly CmmdComment[];
  readonly attachments: readonly CmmdAttachment[];
  readonly contextWarnings: readonly string[];
}> {
  const contextWarnings: string[] = [];
  let comments: readonly CmmdComment[] = cmmdTask?.comments ?? [];
  let attachments: readonly CmmdAttachment[] = cmmdTask?.attachments ?? [];

  const [loadedComments, loadedAttachments] = await Promise.allSettled([
    input.loadTaskComments(input.rawTaskId),
    input.loadTaskAttachments(input.rawTaskId),
  ]);

  if (loadedComments.status === "fulfilled") {
    comments = loadedComments.value;
  } else {
    contextWarnings.push("Task comments could not be loaded before thread start.");
  }

  if (loadedAttachments.status === "fulfilled") {
    attachments = loadedAttachments.value;
  } else {
    contextWarnings.push("Task attachments could not be loaded before thread start.");
  }

  return { comments, attachments, contextWarnings };
}
